import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import actions from "@/actions";
import { COMMENT_NUM_PER_PAGE } from "@/components/comment/constants/comment";
import { CommentState } from "@/components/comment/reducers/comment";
import { calculateStartPos, getPageFromQueryParam } from "@/lib/pager";
import { replaceQueryParams } from "@/lib/urlHelper";
import { RootState } from "@/reducers/types";

type Props = {
  limit: number; // 1ページに表示する件数
  total: number; // データの件数
};

export const usePager = ({ limit, total }: Props) => {
  const dispatch = useDispatch();

  const { lastGetCommentsParams } = useSelector<RootState, CommentState>((state: RootState) => state.comment);
  const [current, setCurrent] = useState(1);
  const maxPage = useMemo(() => Math.ceil(total / limit), [limit, total]);

  const searchPage = (currentPage, retry?: boolean) => {
    const params = {
      ...lastGetCommentsParams,
      start: calculateStartPos(currentPage, limit),
      limit: COMMENT_NUM_PER_PAGE,
    };
    dispatch(
      actions.comment.getComments(params, (res) => {
        if (retry && res.list.length === 0 && 0 < res.total_num && res.total_num < params.start) {
          const maxPage = Math.ceil(res.total_num / COMMENT_NUM_PER_PAGE);
          const fixedParams = { ...params, start: calculateStartPos(maxPage, COMMENT_NUM_PER_PAGE) };
          setCurrent(maxPage);
          dispatch(actions.comment.getComments(fixedParams));
        }
      })
    );
  };

  const searchPageRef = useRef(searchPage);
  useEffect(() => {
    searchPageRef.current = searchPage;
  }, [searchPage]);
  useEffect(() => {
    const page = getPageFromQueryParam("p");
    if (page) {
      setCurrent(page);
    }

    const handleHashChange = () => {
      const pageFromUrl = getPageFromQueryParam("p") || 1;
      setCurrent(pageFromUrl);
      searchPageRef.current(pageFromUrl, true);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  useEffect(() => {
    replaceQueryParams({ p: current });
  }, [current]);

  const prev = () => {
    if (current === 1) return;

    const prevPage = current - 1;
    setCurrent(prevPage);
    searchPage(prevPage);
  };

  const next = () => {
    if (current === maxPage) return;

    const nextPage = current + 1;
    setCurrent(nextPage);
    searchPage(nextPage);
  };

  const jump = (page: number) => {
    if (page < 1 || maxPage < page) return;

    setCurrent(page);
    searchPage(page);
  };

  const init = () => {
    setCurrent(1);
  };

  return {
    current,
    setCurrent, // TODO: ページ制御が複雑になってきた
    maxPage,
    prev,
    next,
    jump,
    init,
  };
};
